import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import {
  loginResponseSchema,
  meResponseSchema,
  type LoginRequest,
  type MeResponse,
} from '@netdeck/contracts';
import { z } from 'zod';
import { apiRequest, refreshAccessToken } from '../../shared/api/client';
import { authStore } from '../../shared/api/auth-store';

export const meQueryKey = ['auth', 'me'] as const;

/**
 * Профиль текущего пользователя.
 *
 * Права берутся отсюда, а не из токена: сервер отдаёт их по актуальным ролям,
 * и отзыв роли действует немедленно. Интерфейс прячет действия ровно по тому
 * же набору, по которому сервер вернёт 403.
 */
export function useMe(enabled: boolean): UseQueryResult<MeResponse, Error> {
  return useQuery({
    queryKey: meQueryKey,
    queryFn: () => apiRequest('/auth/me', meResponseSchema),
    enabled,
    retry: false,
    staleTime: 60_000,
  });
}

export function useLogin() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (credentials: LoginRequest) =>
      apiRequest('/auth/login', loginResponseSchema, {
        method: 'POST',
        body: credentials,
        // На самом входе обновлять нечего: 401 здесь означает неверный пароль.
        skipRefresh: true,
      }),
    onSuccess: (result) => {
      authStore.set(result.accessToken);
      void queryClient.invalidateQueries({ queryKey: meQueryKey });
    },
  });
}

export function useLogout() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () =>
      apiRequest('/auth/logout', z.unknown(), {
        method: 'POST',
        skipRefresh: true,
        acceptStatuses: [204],
      }),
    // Токен сбрасывается в любом случае: даже если сервер не ответил,
    // пользователь нажал «Выйти» и должен выйти.
    onSettled: () => {
      authStore.clear();
      queryClient.clear();
    },
  });
}

export function useChangePassword() {
  return useMutation({
    mutationFn: (input: { currentPassword: string; newPassword: string }) =>
      apiRequest('/auth/change-password', z.unknown(), {
        method: 'POST',
        body: input,
        acceptStatuses: [204],
      }),
  });
}

/**
 * Восстановление сессии при загрузке страницы.
 *
 * Access-токен живёт только в памяти (§10.1), поэтому после перезагрузки его
 * нет. Refresh-токен в httpOnly-cookie при этом остаётся — по нему сессия
 * и восстанавливается, иначе обновление страницы выбрасывало бы на вход.
 */
export async function restoreSession(): Promise<boolean> {
  // Входа не было или из него вышли — спрашивать нечего: refresh-токена нет,
  // и запрос вернёт 401.
  if (!authStore.mayHaveSession) {
    return false;
  }

  /*
    Через общий промис обновления, а не своим запросом.

    Ротация refresh-токенов (§10.1) считает два параллельных обновления
    повторным использованием и отзывает всё семейство — то есть выкидывает
    пользователя из системы. А два вызова здесь получаются легко: в режиме
    строгой проверки React вызывает эффект дважды, да и двух вкладок,
    открытых разом, достаточно.
  */
  return refreshAccessToken();
}
